import { useState, useEffect, useMemo } from 'react';
import { challengesService, Challenge, UserChallenge, ChallengeStats } from '../services/challengesService';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/ui/Card';
import BadgeIcon from '../components/BadgeIcon';
import { translateTargetType } from '../utils/challengeTranslations';
import { useAuth } from '../contexts/AuthContext';
import {
  Trophy,
  Award,
  Target,
  CheckCircle2,
  Zap,
  Medal,
  HelpCircle,
  Filter
} from 'lucide-react';

const DIFFICULTY_CONFIG = {
  EASY: {
    label: 'Ușor',
    color: 'bg-green-50 text-green-700 border-green-200',
    gradient: 'from-green-500 to-emerald-500'
  },
  MEDIUM: {
    label: 'Mediu',
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    gradient: 'from-yellow-500 to-orange-500'
  },
  HARD: {
    label: 'Dificil',
    color: 'bg-red-50 text-red-700 border-red-200',
    gradient: 'from-red-500 to-pink-500'
  },
};

export default function ChallengesPage() {
  const { user } = useAuth();
  const [availableChallenges, setAvailableChallenges] = useState<Challenge[]>([]);
  const [myChallenges, setMyChallenges] = useState<UserChallenge[]>([]);
  const [stats, setStats] = useState<ChallengeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'available' | 'my'>('my');

  // Filter challenges by lifestyleMode - show appropriate difficulty
  const filteredChallenges = useMemo(() => {
    if (!user?.lifestyleMode) return availableChallenges;

    // HEALTHY users → All challenges (они могут делать все)
    if (user.lifestyleMode === 'HEALTHY') {
      return availableChallenges;
    }

    // CHRONIC users → Only EASY and MEDIUM (gentle challenges)
    // Filter out HARD challenges for people with chronic conditions
    return availableChallenges.filter(c => c.difficulty !== 'HARD');
  }, [availableChallenges, user?.lifestyleMode]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [available, my, statsData] = await Promise.all([
        challengesService.getAllChallenges(),
        challengesService.getMyChallenges(),
        challengesService.getStats(),
      ]);
      setAvailableChallenges(available);
      setMyChallenges(my);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (challengeId: string) => {
    try {
      await challengesService.enrollChallenge(challengeId);
      loadData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Eroare la înscriere');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <PageHeader title="Provocări" />
        <div className="p-4">
          <div className="text-center py-8">Se încarcă...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title="Provocări" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        {/* Info Card */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <HelpCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-1">Cum Funcționează?</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong>1.</strong> Înscrie-te la o provocare<br />
                <strong>2.</strong> Lucrează la obiectivele tale din pagina "Obiective"<br />
                <strong>3.</strong> Progresul se actualizează automat când completezi sau actualizezi obiective<br />
                <strong>4.</strong> Câștigi puncte și insigne când finalizezi provocarea! 🏆
              </p>
            </div>
          </div>
        </Card>

        {/* Stats Section */}
        {stats && (
          <div className="grid grid-cols-2 gap-3">
            <Card className="text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <div className="flex justify-center mb-2">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Trophy className="w-7 h-7 text-purple-600" />
                </div>
              </div>
              <div className="text-2xl font-bold text-purple-700">{stats.totalPoints}</div>
              <div className="text-xs font-medium text-purple-600">Puncte Totale</div>
            </Card>
            <Card className="text-center bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
              <div className="flex justify-center mb-2">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                  <Award className="w-7 h-7 text-pink-600" />
                </div>
              </div>
              <div className="text-2xl font-bold text-pink-700">{stats.badgeCount}</div>
              <div className="text-xs font-medium text-pink-600">Insigne Câștigate</div>
            </Card>
          </div>
        )}

        {/* Badges Display */}
        {stats && stats.badges.length > 0 && (
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <Medal className="w-5 h-5 text-yellow-600" />
              <h3 className="font-bold text-gray-900">Insignele Tale</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.badges.map((badge, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-gradient-to-r from-yellow-50 to-orange-50 px-3 py-2 rounded-lg border border-yellow-300"
                >
                  <div className="p-2 bg-white rounded-lg">
                    <BadgeIcon iconName={badge.badgeIcon} className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{badge.badgeName}</div>
                    <div className="text-xs text-gray-600">
                      {new Date(badge.earnedAt).toLocaleDateString('ro-RO')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Challenge Filter Info Banner */}
        {user?.lifestyleMode === 'CHRONIC' && activeTab === 'available' && (
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <Filter className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">Provocări Adaptate Pentru Tine</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Afișăm doar provocări <strong>Ușoare</strong> și <strong>Medii</strong>, adaptate pentru persoane cu condiții cronice. 
                  Provocările dificile sunt ascunse automat pentru siguranța ta. 💚
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 bg-white rounded-lg p-1">
          <button
            onClick={() => setActiveTab('my')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition ${
              activeTab === 'my'
                ? 'bg-purple-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Provocările Mele ({myChallenges.length})
          </button>
          <button
            onClick={() => setActiveTab('available')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition ${
              activeTab === 'available'
                ? 'bg-purple-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Disponibile ({filteredChallenges.length})
          </button>
        </div>

        {/* My Challenges */}
        {activeTab === 'my' && (
          <>
            {myChallenges.length === 0 ? (
              <Card className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                    <Trophy className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Nicio provocare încă
                </h3>
                <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                  Înscrie-te la prima ta provocare și începe să câștigi puncte!
                </p>
                <button
                  onClick={() => setActiveTab('available')}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-purple-800 font-medium shadow-lg transition-all"
                >
                  <Target className="w-5 h-5" />
                  Vezi Provocările
                </button>
              </Card>
            ) : (
              myChallenges.map((uc) => {
                const difficultyConfig = DIFFICULTY_CONFIG[uc.challenge.difficulty as keyof typeof DIFFICULTY_CONFIG];
                const progressPercent = Math.min((uc.progress / uc.challenge.targetCount) * 100, 100);
                
                return (
                  <Card key={uc.id} className="relative overflow-hidden hover:shadow-lg transition-shadow">
                    {/* Gradient accent */}
                    <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${difficultyConfig?.gradient}`} />
                    
                    {/* Completed Badge */}
                    {uc.isCompleted && (
                      <div className="absolute top-4 right-4">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      </div>
                    )}

                    <div className="pl-4">
                      {/* Header */}
                      <div className="flex items-start gap-4 mb-4">
                        <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl">
                          <BadgeIcon iconName={uc.challenge.badgeIcon} className="w-8 h-8 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">{uc.challenge.title}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2">{uc.challenge.description}</p>
                          <div className="flex gap-2 mt-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${difficultyConfig?.color}`}>
                              {difficultyConfig?.label}
                            </span>
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                              <Trophy className="w-3 h-3" />
                              {uc.challenge.pointsReward}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Progress */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Progres</span>
                          <span className="text-sm font-bold text-gray-900">
                            {uc.progress} / {uc.challenge.targetCount}
                          </span>
                        </div>
                        <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`absolute top-0 left-0 h-full bg-gradient-to-r ${difficultyConfig?.gradient} transition-all duration-500 ease-out`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                      </div>

                      {/* Completion Status */}
                      {uc.isCompleted && uc.completedAt && (
                        <div className="flex items-center gap-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Completat: {new Date(uc.completedAt).toLocaleDateString('ro-RO')}
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })
            )}
          </>
        )}

        {/* Available Challenges */}
        {activeTab === 'available' && (
          <>
            {filteredChallenges.length === 0 ? (
              <Card className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                    <Target className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
                <p className="text-gray-600">Nicio provocare disponibilă momentan</p>
              </Card>
            ) : (
              filteredChallenges.map((challenge) => {
                const isEnrolled = myChallenges.some(uc => uc.challengeId === challenge.id);
                const difficultyConfig = DIFFICULTY_CONFIG[challenge.difficulty as keyof typeof DIFFICULTY_CONFIG];
                
                return (
                  <Card key={challenge.id} className="relative overflow-hidden hover:shadow-lg transition-shadow">
                    <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${difficultyConfig?.gradient}`} />
                    
                    <div className="pl-4">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl">
                          <BadgeIcon iconName={challenge.badgeIcon} className="w-8 h-8 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">{challenge.title}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2">{challenge.description}</p>
                          <div className="flex gap-2 mt-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${difficultyConfig?.color}`}>
                              {difficultyConfig?.label}
                            </span>
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                              <Trophy className="w-3 h-3" />
                              {challenge.pointsReward}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-700 mb-3 bg-gray-50 rounded-lg px-3 py-2">
                        <Target className="w-4 h-4 text-gray-600" />
                        <span>Obiectiv: <strong>{challenge.targetCount} {translateTargetType(challenge.targetType)}</strong></span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-700 mb-4 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
                        <Award className="w-4 h-4 text-amber-600" />
                        <span>Insignă: <strong>{challenge.badgeName}</strong></span>
                      </div>

                      {isEnrolled ? (
                        <button
                          disabled
                          className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-500 py-2.5 rounded-lg cursor-not-allowed border border-gray-200"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Deja Înscris
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEnroll(challenge.id)}
                          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white py-2.5 rounded-lg hover:from-purple-700 hover:to-purple-800 font-medium shadow-md transition-all"
                        >
                          <Zap className="w-4 h-4" />
                          Înscrie-te
                        </button>
                      )}
                    </div>
                  </Card>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
}
